import type { Tela, TelaDeOrigem } from "./Aplicacao"
import { TELAS, type ChaveDeTela } from "./registro-de-telas"
import type { AbaDaFicha } from "./telas/Ficha"
import { FILTROS_DO_ITEM, itemVisivel, type GrupoDeConfiguracao } from "./itens-de-configuracao"

/* A ORIGEM DO CONTRATO ABERTO (spec 0084), em funções puras: a ficha guarda a
   tela de onde foi aberta, o menu marca essa tela, e o voltar da ficha leva a
   ela. A trilha usa a mesma origem (`casca/trilha.ts`). */

/** A ficha aberta a partir de `atual`. De uma tela comum, ela vira a origem;
 *  de dentro de uma ficha ou de um processo, vale a origem que já havia. */
export function fichaAbertaDe(atual: Tela, contratoId: number, aba: AbaDaFicha): Extract<Tela, { nome: "ficha" }> {
  const origem: TelaDeOrigem | undefined =
    atual.nome === "ficha" || atual.nome === "esteira" ? atual.origem : atual
  return { nome: "ficha", contratoId, aba, submenu: {}, ...(origem === undefined ? {} : { origem }) }
}

/** O item marcado no menu: na ficha e no processo, a origem; sem ela, Contratos. */
export function itemDoMenu(tela: Tela): ChaveDeTela {
  if (tela.nome === "ficha" || tela.nome === "esteira") return tela.origem?.nome ?? "contratos"
  return tela.nome
}

/** Para onde o voltar da ficha leva. */
export function voltarDaFicha(tela: Extract<Tela, { nome: "ficha" }>): TelaDeOrigem {
  return tela.origem ?? { nome: "contratos" }
}

/** O nome da origem no menu, para o botão de voltar da ficha. */
export function rotuloDaOrigem(tela: Extract<Tela, { nome: "ficha" }>): string {
  return TELAS[voltarDaFicha(tela).nome].rotulo
}

/** A tela de Configurações com o item que a conta vê (spec 0104): sem item, ou
 *  com um que ela não vê, abre o primeiro visto; sem nenhum, vai ao Painel.
 *  Sem o alcance ainda, nada muda. Devolve a mesma tela quando não há o que
 *  corrigir, para quem guarda o estado não redesenhar à toa. */
export function telaNormalizada(tela: Tela, grupos: GrupoDeConfiguracao[] | null): Tela {
  if (tela.nome !== "configuracoes" || grupos === null) return tela
  const pedido = "item" in tela ? tela.item : undefined
  const item = itemVisivel(grupos, pedido)
  if (item === undefined) return { nome: "painel" }
  const entidade = "entidade" in tela ? tela.entidade : undefined
  if (item !== pedido) return { nome: "configuracoes", item }
  if (entidade !== undefined && item !== "entidades") return { nome: "configuracoes", item }
  const aceitos = FILTROS_DO_ITEM[item] ?? []
  const comoRegistro = tela as Record<string, unknown>
  const sobrando = (["perfil", "cargo", "tipo", "sistema"] as const).filter((chave) => comoRegistro[chave] !== undefined && !aceitos.includes(chave))
  if (sobrando.length > 0) {
    const limpa: Record<string, unknown> = { ...tela }
    for (const chave of sobrando) delete limpa[chave]
    return limpa as Tela
  }
  return tela
}

/** Como a troca de tela entra no histórico do navegador. Na mesma tela (ou no
 *  mesmo contrato), substitui; de um item de Configurações a outro, empilha,
 *  para o voltar percorrer os itens (spec 0104). */
export function comoGravarNoHistorico(anterior: Tela, nova: Tela): "push" | "replace" {
  if (nova.nome !== anterior.nome) return "push"
  if (nova.nome === "configuracoes") {
    const antes = "item" in anterior ? anterior.item : undefined
    const depois = "item" in nova ? nova.item : undefined
    const entidadeAntes = "entidade" in anterior ? anterior.entidade : undefined
    const entidadeDepois = "entidade" in nova ? nova.entidade : undefined
    if (antes !== undefined && depois !== undefined && entidadeAntes !== entidadeDepois) return "push"
    return antes !== undefined && depois !== undefined && antes !== depois ? "push" : "replace"
  }
  if (nova.nome === "ficha" || nova.nome === "esteira") {
    return "contratoId" in anterior && anterior.contratoId === nova.contratoId ? "replace" : "push"
  }
  return "replace"
}
